import { Button, Flex, Input, Spinner, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'

import { Modal, SearchableCombobox } from '@mangovoice/mango-ui'
import type { SearchableComboboxOption, ToastLike } from '@mangovoice/mango-ui'
import type { AvailablePhoneNumber, PbxLocation } from '@mangovoice/types'

import type { PbxLocationsApi } from '../api/pbxLocationsApi.types'
import {
  useCreatePbxLocationMutation,
  useGetAvailablePhoneNumbersQuery,
  useUpdatePbxLocationMutation,
} from '../queries'
import { text as tfallback } from '../types'
import type { PbxLocationsTranslateFn } from '../types'
import { composeApiErrorMessage } from '../utils/pbxLocations.utils'

interface LocationFormModalProps {
  api: PbxLocationsApi
  isOpen: boolean
  onClose: () => void
  pbxId: number
  /** When defined, the modal is in edit mode. */
  location?: PbxLocation
  onSaved: () => void
  toast: ToastLike
  t?: PbxLocationsTranslateFn
}

const formatPhoneNumberOptionLabel = (number: AvailablePhoneNumber): string => {
  const numberLabel = number.number_for_display || number.number
  const addressParts = [
    number.e911_address,
    number.e911_city,
    number.e911_state,
    number.e911_postal_code,
  ].filter(Boolean)
  const address = addressParts.join(', ')
  return address ? `${numberLabel} — ${address}` : numberLabel
}

export const LocationFormModal: React.FC<LocationFormModalProps> = ({
  api,
  isOpen,
  onClose,
  pbxId,
  location,
  onSaved,
  toast,
  t,
}) => {
  const isEditMode = Boolean(location)
  const [name, setName] = useState<string>(location?.name ?? '')
  const [phoneNumberId, setPhoneNumberId] = useState<number | null>(
    location?.phone_number?.id ?? null,
  )
  const [nameError, setNameError] = useState<string | null>(null)
  const [appliedDefaultPhoneNumber, setAppliedDefaultPhoneNumber] =
    useState(false)

  const {
    data: availablePhoneNumbers,
    isFetching: isLoadingAvailableNumbers,
    refetch: refetchAvailableNumbers,
  } = useGetAvailablePhoneNumbersQuery({
    api,
    pbxId,
    excludeLocationId: location?.id,
    options: {
      enabled: false,
    },
  })

  useEffect(() => {
    if (!isOpen) return
    setName(location?.name ?? '')
    setPhoneNumberId(location?.phone_number?.id ?? null)
    setNameError(null)
    setAppliedDefaultPhoneNumber(false)
    refetchAvailableNumbers()
  }, [isOpen, location?.id, refetchAvailableNumbers])

  // Mirror admin2: when editing a location with no phone number assigned,
  // pre-select the first available number so the admin can confirm with one
  // click.
  useEffect(() => {
    if (!isOpen) return
    if (appliedDefaultPhoneNumber) return
    if (!isEditMode) return
    if (location?.phone_number) return
    if (!availablePhoneNumbers || availablePhoneNumbers.length === 0) return
    setPhoneNumberId(availablePhoneNumbers[0].id)
    setAppliedDefaultPhoneNumber(true)
  }, [
    appliedDefaultPhoneNumber,
    availablePhoneNumbers,
    isEditMode,
    isOpen,
    location?.phone_number,
  ])

  const comboboxOptions = useMemo<SearchableComboboxOption[]>(() => {
    const numbers = availablePhoneNumbers ?? []
    const items: SearchableComboboxOption[] = numbers.map((number) => ({
      id: String(number.id),
      label: formatPhoneNumberOptionLabel(number),
    }))
    // Preserve the currently-assigned number even if the API omitted it from
    // available-numbers (which filters out numbers attached to other
    // locations). Keeps edit mode showing a sensible label.
    if (
      location?.phone_number &&
      !numbers.some((n) => n.id === location.phone_number?.id)
    ) {
      items.unshift({
        id: String(location.phone_number.id),
        label: formatPhoneNumberOptionLabel({
          id: location.phone_number.id,
          number: location.phone_number.number,
          number_for_display: location.phone_number.number_for_display,
          e911_address: location.phone_number.e911_address,
          e911_city: location.phone_number.e911_city,
          e911_state: location.phone_number.e911_state,
          e911_postal_code: location.phone_number.e911_postal_code,
        }),
      })
    }
    return items
  }, [availablePhoneNumbers, location?.phone_number])

  const createMutation = useCreatePbxLocationMutation(api, {
    onSuccess: () => {
      toast.create({
        title: tfallback(
          t,
          'pbxLocations.form.createSuccess',
          'Location created.',
        ),
        type: 'success',
      })
      onSaved()
      onClose()
    },
    onError: (error) => {
      toast.create({
        title: composeApiErrorMessage(
          error,
          tfallback(
            t,
            'pbxLocations.form.createError',
            'Failed to create location.',
          ),
        ),
        type: 'error',
      })
    },
  })

  const updateMutation = useUpdatePbxLocationMutation(api, {
    onSuccess: () => {
      toast.create({
        title: tfallback(
          t,
          'pbxLocations.form.updateSuccess',
          'Location updated.',
        ),
        type: 'success',
      })
      onSaved()
      onClose()
    },
    onError: (error) => {
      toast.create({
        title: composeApiErrorMessage(
          error,
          tfallback(
            t,
            'pbxLocations.form.updateError',
            'Failed to update location.',
          ),
        ),
        type: 'error',
      })
    },
  })

  const isSaving = createMutation.isPending || updateMutation.isPending

  const handleSave = () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError(
        tfallback(t, 'pbxLocations.form.nameRequired', 'Name is required'),
      )
      return
    }
    setNameError(null)

    if (isEditMode && location) {
      updateMutation.mutate({
        id: location.id,
        body: {
          name: trimmedName,
          phone_number_id: phoneNumberId,
        },
      })
    } else {
      createMutation.mutate({
        pbx_id: pbxId,
        name: trimmedName,
        phone_number_id: phoneNumberId,
      })
    }
  }

  const headerLabel = isEditMode
    ? tfallback(t, 'pbxLocations.form.editHeader', 'Edit Location')
    : tfallback(t, 'pbxLocations.form.createHeader', 'Add Location')

  const submitLabel = isEditMode
    ? tfallback(t, 'pbxLocations.form.update', 'Update Location')
    : tfallback(t, 'pbxLocations.form.create', 'Create Location')

  const phoneNumberContent = isLoadingAvailableNumbers ? (
    <Flex alignItems="center" gap="0.5rem">
      <Spinner size="sm" />
      <Text color="fg.muted" fontSize="sm">
        {tfallback(
          t,
          'pbxLocations.form.loadingAvailableNumbers',
          'Loading available numbers...',
        )}
      </Text>
    </Flex>
  ) : comboboxOptions.length === 0 ? (
    <Text color="fg.muted" fontSize="sm">
      {tfallback(
        t,
        'pbxLocations.form.noAvailableNumbers',
        'No phone numbers are available for assignment.',
      )}
    </Text>
  ) : (
    <SearchableCombobox
      options={comboboxOptions}
      value={phoneNumberId !== null ? String(phoneNumberId) : ''}
      onChange={(value) =>
        setPhoneNumberId(value === '' ? null : Number(value))
      }
      placeholder={tfallback(
        t,
        'pbxLocations.form.phoneNumberPlaceholder',
        'Select a phone number',
      )}
      width="100%"
    />
  )

  return (
    <Modal
      open={isOpen}
      onOpenChange={(d) => {
        if (!d.open) onClose()
      }}
      title={headerLabel}
      maxWidth="560px"
      footer={
        <Flex gap={3} justifyContent="flex-end" width="100%">
          <Button
            variant="outline"
            colorPalette="neutral"
            rounded="lg"
            onClick={onClose}
            disabled={isSaving}
          >
            {tfallback(t, 'pbxLocations.form.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            rounded="lg"
            onClick={handleSave}
            loading={isSaving}
          >
            {submitLabel}
          </Button>
        </Flex>
      }
    >
      <Flex flexDirection="column" gap="1rem">
        <Flex flexDirection="column" gap="0.25rem">
          <Text fontWeight="semibold" fontSize="sm">
            {tfallback(t, 'pbxLocations.form.nameLabel', 'Name')}
          </Text>
          <Input
            value={name}
            placeholder={tfallback(
              t,
              'pbxLocations.form.namePlaceholder',
              'e.g. Main Office',
            )}
            onChange={(e) => {
              setName(e.target.value)
              if (nameError) setNameError(null)
            }}
            maxLength={120}
          />
          {nameError && (
            <Text color="red.fg" fontSize="sm">
              {nameError}
            </Text>
          )}
        </Flex>
        <Flex flexDirection="column" gap="0.25rem">
          <Text fontWeight="semibold" fontSize="sm">
            {tfallback(
              t,
              'pbxLocations.form.phoneNumberLabel',
              'E911 Phone Number',
            )}
          </Text>
          {phoneNumberContent}
          <Text color="fg.muted" fontSize="sm">
            {tfallback(
              t,
              'pbxLocations.form.phoneNumberHelp',
              "This number's E911 address will be used for emergency calls placed from this location.",
            )}
          </Text>
        </Flex>
      </Flex>
    </Modal>
  )
}
